import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import Avatar from "@/components/Avatar";
import { Input } from "@/components/ui/input";
import * as log from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { MessageCircle, Video, X, Search, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { formatLastSeen, formatExact } from "@/lib/presence";

/**
 * Slide-over People panel showing all org members grouped by presence.
 */

export default function PeoplePanel({ open, onClose, presenceMap }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/people");
      setData(data);
    } catch (err) { log.debug("[people] load failed", err); }
  };
  useEffect(() => { if (open) load(); }, [open]);
  useEffect(() => {
    if (!open) return;
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [open]);
  // realtime hint from map
  useEffect(() => { if (open) load(); }, [presenceMap, open]);

  const filtered = useMemo(() => {
    if (!data) return null;
    const term = q.trim().toLowerCase();
    if (!term) return data;
    const f = (arr) => arr.filter((p) => [p.name, p.email, p.department, p.title, p.role].some((v) => (v || "").toLowerCase().includes(term)));
    return { summary: data.summary, groups: { online: f(data.groups.online), meeting: f(data.groups.meeting), away: f(data.groups.away), offline: f(data.groups.offline) } };
  }, [data, q]);

  const startDm = async (uid) => {
    try {
      const { data } = await api.post("/conversations", { user_ids: [uid] });
      nav(`/app/dm/${data.id}`);
      onClose && onClose();
    } catch { toast.error("Failed to open DM"); }
  };
  const startHuddle = async (uid) => {
    try {
      const { data } = await api.post("/calls/start", { room: `dm-${uid}`, kind: "voice" });
      toast.success("Huddle started — opening…");
      window.dispatchEvent(new CustomEvent("tovra:open-call", { detail: data }));
      onClose && onClose();
    } catch { toast.error("Failed to start huddle"); }
  };

  return (
    <aside
      className={`fixed inset-y-0 right-0 z-40 w-[380px] max-w-full bg-white border-l border-zinc-200 shadow-2xl transition-transform duration-200 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}
      role="dialog"
      aria-label="People panel"
      data-testid="people-panel"
    >
      <div className="h-14 border-b flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-zinc-500"/>
          <div className="font-semibold text-[15px]">People</div>
          {data && <span className="text-xs text-zinc-500">· {data.summary.online + data.summary.meeting + data.summary.away} online</span>}
        </div>
        <button onClick={onClose} className="p-1.5 rounded-md hover:bg-zinc-100" data-testid="people-close" aria-label="Close people panel"><X className="w-4 h-4"/></button>
      </div>
      <div className="p-3 border-b">
        <div className="flex items-center gap-2 border border-zinc-200 rounded-md px-2 py-1.5 focus-within:border-zinc-400">
          <Search className="w-3.5 h-3.5 text-zinc-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, role, department…" className="flex-1 text-sm outline-none bg-transparent" data-testid="people-search"/>
        </div>
        {data && (
          <div className="mt-3 grid grid-cols-4 gap-1 text-[10px] tracking-widest uppercase">
            <Pill label="Online" v={data.summary.online} color="#10B981"/>
            <Pill label="In mtg" v={data.summary.meeting} color="#8B5CF6"/>
            <Pill label="Away" v={data.summary.away} color="#F59E0B"/>
            <Pill label="Offline" v={data.summary.offline} color="#9CA3AF"/>
          </div>
        )}
      </div>
      <div className="overflow-y-auto scroll-thin h-[calc(100vh-56px-118px)]">
        {filtered && (
          <>
            <Section title="Online now" items={filtered.groups.online} onDm={startDm} onHuddle={startHuddle} meId={user?.id}/>
            <Section title="In meetings" items={filtered.groups.meeting} onDm={startDm} onHuddle={startHuddle} meId={user?.id}/>
            <Section title="Away" items={filtered.groups.away} onDm={startDm} onHuddle={startHuddle} meId={user?.id} muted/>
            <Section title="Offline" items={filtered.groups.offline} onDm={startDm} meId={user?.id} offline/>
          </>
        )}
      </div>
    </aside>
  );
}

function Pill({ label, v, color }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-zinc-50/70 text-center py-1.5">
      <div className="font-semibold text-sm text-zinc-800 flex items-center justify-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }}/>
        {v}
      </div>
      <div className="text-[9px] text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

function Section({ title, items, onDm, onHuddle, meId, muted, offline }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="py-2">
      <div className="text-[10px] tracking-widest uppercase text-zinc-500 px-4 py-1.5">{title} · {items.length}</div>
      {items.map((p) => {
        const lastSeenLabel = formatLastSeen(p.last_seen_at);
        const lastSeenTitle = p.last_seen_at ? formatExact(p.last_seen_at) : "Never seen online";
        return (
        <div key={p.user_id} className={`flex items-center gap-3 px-4 py-2 hover:bg-zinc-50 ${offline ? "opacity-70" : ""}`} data-testid={`people-row-${p.user_id}`}>
          <Avatar user={p} size="lg" status={p.status} online={p.online} muted={muted || offline} ring/>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{p.name}{p.user_id === meId && <span className="ml-1 text-[10px] tracking-widest text-zinc-400">YOU</span>}</div>
            <div className="text-xs text-zinc-500 truncate" title={offline ? lastSeenTitle : ""}>
              {p.title || p.department || p.role}
              {p.room && <span> · <span className="text-zinc-600">{p.room}</span></span>}
              {offline && (
                <span data-testid={`people-lastseen-${p.user_id}`}>
                  {" · Last seen "}
                  {lastSeenLabel || "never"}
                </span>
              )}
            </div>
          </div>
          {p.user_id !== meId && (
            <div className="flex gap-1 opacity-0 group-hover:opacity-100">
              <button onClick={() => onDm(p.user_id)} className="p-1.5 rounded hover:bg-zinc-200" data-testid={`people-dm-${p.user_id}`} title="Direct message"><MessageCircle className="w-3.5 h-3.5 text-zinc-600"/></button>
              {!offline && onHuddle && <button onClick={() => onHuddle(p.user_id)} className="p-1.5 rounded hover:bg-zinc-200" data-testid={`people-huddle-${p.user_id}`} title="Start huddle"><Video className="w-3.5 h-3.5 text-zinc-600"/></button>}
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
}
